import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import EnquiryFormModal from '../components/EnquiryFormModal';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import './EnquiryList.css';
import { toast } from '../components/Toast';

// ── Date helpers ──────────────────────────────────────────────────────────────
const fmt = (d) => d.toISOString().split('T')[0];

const getPresetRange = (preset) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (preset) {
    case 'today':
      return { from: fmt(today), to: fmt(today) };
    case 'this_week': {
      const day = today.getDay();
      const mon = new Date(today);
      mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
      return { from: fmt(mon), to: fmt(today) };
    }
    case 'this_month':
      return { from: fmt(new Date(y, m, 1)), to: fmt(new Date(y, m + 1, 0)) };
    case 'last_month':
      return { from: fmt(new Date(y, m - 1, 1)), to: fmt(new Date(y, m, 0)) };
    case 'this_quarter': {
      const q = Math.floor(m / 3);
      return { from: fmt(new Date(y, q * 3, 1)), to: fmt(new Date(y, q * 3 + 3, 0)) };
    }
    case 'this_fy': {
      const fyStart = m >= 3 ? y : y - 1;
      return { from: fmt(new Date(fyStart, 3, 1)), to: fmt(new Date(fyStart + 1, 2, 31)) };
    }
    case 'last_fy': {
      const fyStart = (m >= 3 ? y : y - 1) - 1;
      return { from: fmt(new Date(fyStart, 3, 1)), to: fmt(new Date(fyStart + 1, 2, 31)) };
    }
    default:
      return null;
  }
};

const PRESETS = [
  { key: 'today',        label: 'Today' },
  { key: 'this_week',    label: 'This Week' },
  { key: 'this_month',   label: 'This Month' },
  { key: 'last_month',   label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'this_fy',      label: 'This FY' },
  { key: 'last_fy',      label: 'Last FY' },
];

function EnquiryList() {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activePreset, setActivePreset] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [users, setUsers] = useState([]);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return ' ↕';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedEnquiries = React.useMemo(() => {
    let sortable = [...filteredEnquiries];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'customerName') {
          aVal = a.customerName || '';
          bVal = b.customerName || '';
        } else if (sortConfig.key === 'assignedTo') {
          aVal = a.assignedTo?.name || '';
          bVal = b.assignedTo?.name || '';
        } else if (sortConfig.key === 'createdAt') {
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
        }

        if (aVal === undefined || aVal === null) aVal = '';
        if (bVal === undefined || bVal === null) bVal = '';

        if (typeof aVal === 'string') {
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        } else {
          return sortConfig.direction === 'asc'
            ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
            : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
        }
      });
    }
    return sortable;
  }, [filteredEnquiries, sortConfig]);

  useEffect(() => {
    fetchEnquiries();
    fetchUsers();
  }, []);

  useEffect(() => {
    filterEnquiries();
  }, [enquiries, searchTerm, statusFilter, assignedToFilter, dateFrom, dateTo]);

  const fetchEnquiries = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/enquiries');
      const enquiryData = response.data.data?.content || response.data.content || response.data;
      setEnquiries(enquiryData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      const userData = response.data.data?.content || response.data.content || response.data;
      setUsers(userData);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const filterEnquiries = () => {
    let filtered = [...enquiries];

    if (searchTerm) {
      filtered = filtered.filter(e =>
        e.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.phone.includes(searchTerm) ||
        (e.email && e.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        e.id.toString().includes(searchTerm)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(e => e.status === statusFilter);
    }

    if (assignedToFilter) {
      filtered = filtered.filter(e =>
        e.assignedTo && e.assignedTo.id.toString() === assignedToFilter
      );
    }

    if (dateFrom) {
      filtered = filtered.filter(e => new Date(e.createdAt) >= new Date(dateFrom));
    }
    if (dateTo) {
      filtered = filtered.filter(e => new Date(e.createdAt) <= new Date(dateTo + 'T23:59:59'));
    }

    setFilteredEnquiries(filtered);
    setCurrentPage(0);
  };

  const applyPreset = (key) => {
    const range = getPresetRange(key);
    if (!range) return;
    setActivePreset(key);
    setDateFrom(range.from);
    setDateTo(range.to);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setAssignedToFilter('');
    setDateFrom('');
    setDateTo('');
    setActivePreset('');
  };

  const handleCreate = () => { setSelectedEnquiry(null); setShowModal(true); };
  const handleEdit   = (e)  => { setSelectedEnquiry(e);    setShowModal(true); };
  const handleView   = (e)  => navigate(`/enquiries/${e.id}`);

  const handleModalClose = () => { setShowModal(false); setSelectedEnquiry(null); };
  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedEnquiry(null);
    fetchEnquiries();
    showSuccessMsg('Enquiry saved successfully');
  };

  const handleDelete = (enquiry) => {
    setEnquiryToDelete(enquiry);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!enquiryToDelete) return;
    try {
      await axios.delete(`/enquiries/${enquiryToDelete.id}`);
      setShowConfirmModal(false);
      setEnquiryToDelete(null);
      fetchEnquiries();
      showSuccessMsg('Enquiry deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete enquiry');
      setShowConfirmModal(false);
      setEnquiryToDelete(null);
    }
  };

  const showSuccessMsg = (msg) => {
    toast.success(msg);
  };

  const getStatusBadgeClass = (status) => ({
    NEW:                'badge-new',
    FOLLOWUP:           'badge-followup',
    CUSTOMER_CONVERTED: 'badge-converted',
    PROJECT_CONVERTED:  'badge-converted',
    LOST:               'badge-lost',
  }[status] || '');

  return (
    <div className="enquiry-list-container">
      <div className="page-header">
        <h1>Enquiry Management</h1>
        <button className="btn btn-primary" onClick={handleCreate}>Create Enquiry</button>
      </div>

      {/* Quick date presets */}
      <div className="enq-date-presets">
        {PRESETS.map(p => (
          <button
            key={p.key}
            className={`enq-preset-chip ${activePreset === p.key ? 'active' : ''}`}
            onClick={() => applyPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="enq-filter-row">
          <label className="enq-filter-label">Search</label>
          <input
            type="text"
            placeholder="Search by ID, customer name, phone, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="enq-filter-row">
          <label className="enq-filter-label">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
            <option value="">All Status</option>
            <option value="NEW">New</option>
            <option value="FOLLOWUP">Follow-up</option>
            <option value="CUSTOMER_CONVERTED">Customer Converted</option>
            <option value="PROJECT_CONVERTED">Project Converted</option>
            <option value="LOST">Lost</option>
          </select>
        </div>
        <div className="enq-filter-row">
          <label className="enq-filter-label">Assigned To</label>
          <select value={assignedToFilter} onChange={(e) => setAssignedToFilter(e.target.value)} className="filter-select">
            <option value="">All Assigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div className="enq-filter-row">
          <label className="enq-filter-label">From Date</label>
          <input type="date" value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setActivePreset(''); }}
            className="filter-input" />
        </div>
        <div className="enq-filter-row">
          <label className="enq-filter-label">To Date</label>
          <input type="date" value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setActivePreset(''); }}
            className="filter-input" />
        </div>
        <button className="btn btn-secondary btn-sm enq-clear-btn" onClick={clearFilters}>
          Clear Filters
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading enquiries...</div>
      ) : (
        <div className="table-container">
          <table className="enquiry-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('customerName')} style={{ cursor: 'pointer', userSelect: 'none' }}>Customer Name {getSortIcon('customerName')}</th>
                <th onClick={() => requestSort('phone')} style={{ cursor: 'pointer', userSelect: 'none' }}>Phone {getSortIcon('phone')}</th>
                <th onClick={() => requestSort('email')} style={{ cursor: 'pointer', userSelect: 'none' }}>Email {getSortIcon('email')}</th>
                <th onClick={() => requestSort('source')} style={{ cursor: 'pointer', userSelect: 'none' }}>Source {getSortIcon('source')}</th>
                <th onClick={() => requestSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>Status {getSortIcon('status')}</th>
                <th onClick={() => requestSort('assignedTo')} style={{ cursor: 'pointer', userSelect: 'none' }}>Assigned To {getSortIcon('assignedTo')}</th>
                <th onClick={() => requestSort('createdAt')} style={{ cursor: 'pointer', userSelect: 'none' }}>Created At {getSortIcon('createdAt')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEnquiries.length === 0 ? (
                <tr><td colSpan="8" className="no-data">No enquiries found</td></tr>
              ) : (
                sortedEnquiries.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage).map((enquiry) => (
                  <tr key={enquiry.id}>
                    <td data-label="Customer Name"><strong>{enquiry.customerName}</strong></td>
                    <td data-label="Phone">{enquiry.phone}</td>
                    <td data-label="Email">{enquiry.email || '-'}</td>
                    <td data-label="Source">{enquiry.source || '-'}</td>
                    <td data-label="Status">
                      <span className={`badge ${getStatusBadgeClass(enquiry.status)}`}>
                        {enquiry.status}
                      </span>
                    </td>
                    <td data-label="Assigned To">{enquiry.assignedTo ? enquiry.assignedTo.name : '-'}</td>
                    <td data-label="Created At">{new Date(enquiry.createdAt).toLocaleDateString()}</td>
                    <td data-label="Actions">
                      <div className="action-buttons">
                        <button className="btn btn-sm btn-primary"   onClick={() => handleView(enquiry)}>View</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(enquiry)}>Edit</button>
                        <button className="btn btn-sm btn-danger"    onClick={() => handleDelete(enquiry)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {Math.ceil(filteredEnquiries.length / itemsPerPage) > 1 && (
            <div className="pagination" style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage + 1} of {Math.ceil(filteredEnquiries.length / itemsPerPage)} ({filteredEnquiries.length} total)
              </span>
              <button
                className="btn btn-sm"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredEnquiries.length / itemsPerPage) - 1, prev + 1))}
                disabled={currentPage >= Math.ceil(filteredEnquiries.length / itemsPerPage) - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {showModal && (
        <EnquiryFormModal
          enquiry={selectedEnquiry}
          users={users}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      <DeleteConfirmModal
        isOpen={showConfirmModal}
        title="Delete Enquiry"
        message={<>Are you sure you want to delete the enquiry from <strong>{enquiryToDelete?.customerName}</strong>?</>}
        onCancel={() => { setShowConfirmModal(false); setEnquiryToDelete(null); }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

export default EnquiryList;
